/**
 * Renders an assistant reply as DOM nodes: paragraphs, lists, bold, inline
 * code, and links (URLs, emails, phone numbers).
 *
 * Everything is built with createElement/createTextNode rather than an HTML
 * string. The text comes from a model summarising third-party page content, so
 * it is never trusted and must never be parsed as markup.
 */

/** Markdown link, bare URL, email, or phone number in priority order. */
const INLINE_PATTERN =
  /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(\*\*([^*]+)\*\*)|(`([^`]+)`)|(https?:\/\/[^\s<>"')\]]+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})|(\+?\d[\d\s().-]{7,}\d)/gi;

/** Trailing punctuation that belongs to the sentence, not the URL. */
function splitTrailing(value: string): { core: string; trailing: string } {
  const match = value.match(/[.,;:!?)\]]+$/);
  if (!match) return { core: value, trailing: '' };
  return { core: value.slice(0, -match[0].length), trailing: match[0] };
}

function anchor(href: string, label: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.className = 'maicb-link';
  link.href = href;
  link.textContent = label;
  if (/^https?:/i.test(href)) {
    link.target = '_blank';
    // Deny the linked page a handle on the host window via window.opener.
    link.rel = 'noopener noreferrer';
  }
  return link;
}

/** A phone number needs enough digits to be real, not a year or an order id. */
function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 9 && digits.length <= 15;
}

/** Renders one line of inline markdown into `container`. */
function appendInline(container: HTMLElement, text: string): void {
  let lastIndex = 0;

  for (const match of text.matchAll(INLINE_PATTERN)) {
    const [full, mdLabel, mdHref, , boldText, , codeText, bareUrl, email, phone] =
      match;
    const start = match.index ?? 0;

    if (start > lastIndex) {
      container.appendChild(document.createTextNode(text.slice(lastIndex, start)));
    }

    if (mdHref) {
      const { core, trailing } = splitTrailing(mdHref);
      container.appendChild(anchor(core, mdLabel));
      if (trailing) container.appendChild(document.createTextNode(trailing));
    } else if (boldText) {
      const strong = document.createElement('strong');
      strong.textContent = boldText;
      container.appendChild(strong);
    } else if (codeText) {
      const code = document.createElement('code');
      code.className = 'maicb-code';
      code.textContent = codeText;
      container.appendChild(code);
    } else if (bareUrl) {
      const { core, trailing } = splitTrailing(bareUrl);
      const label = core.replace(/^https?:\/\//, '').replace(/\/$/, '');
      container.appendChild(anchor(core, label));
      if (trailing) container.appendChild(document.createTextNode(trailing));
    } else if (email) {
      const { core, trailing } = splitTrailing(email);
      container.appendChild(anchor(`mailto:${core}`, core));
      if (trailing) container.appendChild(document.createTextNode(trailing));
    } else if (phone && looksLikePhone(phone)) {
      const trimmed = phone.trim();
      container.appendChild(anchor(`tel:${trimmed.replace(/[\s().-]/g, '')}`, trimmed));
    } else {
      // Matched but rejected (e.g. a number that isn't a phone) keep as text.
      container.appendChild(document.createTextNode(full));
    }

    lastIndex = start + full.length;
  }

  if (lastIndex < text.length) {
    container.appendChild(document.createTextNode(text.slice(lastIndex)));
  }
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;

/**
 * Renders a full reply, grouping consecutive bullets or numbered items into
 * real lists and separating blank-line-delimited blocks into paragraphs.
 */
export function appendLinkedText(container: HTMLElement, text: string): void {
  const lines = text.split('\n');
  let list: HTMLUListElement | HTMLOListElement | null = null;
  let paragraph: HTMLParagraphElement | null = null;

  const closeList = () => { list = null; };
  const closeParagraph = () => { paragraph = null; };

  for (const line of lines) {
    const bullet = line.match(BULLET);
    const numbered = line.match(NUMBERED);

    if (bullet || numbered) {
      closeParagraph();
      const ordered = Boolean(numbered);
      // Start a new list when there isn't one, or the type changed.
      if (!list || (list.tagName === 'OL') !== ordered) {
        list = document.createElement(ordered ? 'ol' : 'ul');
        list.className = 'maicb-list';
        container.appendChild(list);
      }
      const item = document.createElement('li');
      appendInline(item, (bullet ? bullet[1] : numbered![2]).trim());
      list.appendChild(item);
      continue;
    }

    if (line.trim() === '') {
      closeList();
      closeParagraph();
      continue;
    }

    closeList();
    if (!paragraph) {
      paragraph = document.createElement('p');
      paragraph.className = 'maicb-para';
      container.appendChild(paragraph);
    } else {
      // A soft wrap inside the same paragraph.
      paragraph.appendChild(document.createTextNode(' '));
    }
    appendInline(paragraph, line.trim());
  }
}
